import { ModalDisplay, type ModalDisplayOptions } from '../features/modal-window';

export type ModalWindowMountAllOptions = ModalDisplayOptions & {
  /**
   * Modal Window の接続対象になる root 要素です。
   * 未指定時は data-modal-root を探します。
   */
  rootSelector?: string;
};

const MODAL_WINDOW_ROOT_SELECTOR = '[data-modal-root]';
const gModalWindowInstances: WeakMap<HTMLElement, ModalDisplay> = new WeakMap();

export function mountModalWindow(
  root: HTMLElement | null,
  options: ModalDisplayOptions = {},
): void
{
  if (!root) return;

  const current_instance: ModalDisplay | undefined = gModalWindowInstances.get(root);

  if (current_instance)
  {
    current_instance.destroy();
    gModalWindowInstances.delete(root);
  }

  const modal_display: ModalDisplay = new ModalDisplay(document.body, root, options);
  modal_display.runProgram();
  gModalWindowInstances.set(root, modal_display);
}

/**
 * // Function: scope 内の Modal Window root にまとめて接続
 */
export function mountModalWindowAll(
  scope: Document | HTMLElement = document,
  options: ModalWindowMountAllOptions = {},
): void
{
  const { rootSelector = MODAL_WINDOW_ROOT_SELECTOR, ...modal_options } = options;
  const modal_roots: HTMLElement[] = collectModalWindowRoots(scope, rootSelector);

  modal_roots.forEach((modal_root: HTMLElement) =>
  {
    mountModalWindow(modal_root, modal_options);
  });
}

/**
 * // Function: 単一 Modal Window をアンマウント
 */
export function unmountModalWindow(root: HTMLElement | null): void
{
  if (!root)
  {
    return;
  }

  const current_instance: ModalDisplay | undefined = gModalWindowInstances.get(root);

  if (!current_instance)
  {
    return;
  }

  current_instance.destroy();
  gModalWindowInstances.delete(root);
}

/**
 * // Function: 単一 Modal Window を破棄して再接続
 */
export function remountModalWindow(
  root: HTMLElement | null,
  options: ModalDisplayOptions = {},
): void
{
  unmountModalWindow(root);
  mountModalWindow(root, options);
}

/**
 * // Function: root に接続済みの ModalDisplay instance を取得
 */
export function getMountedModalWindow(root: HTMLElement | null): ModalDisplay | null
{
  if (!root)
  {
    return null;
  }

  return gModalWindowInstances.get(root) ?? null;
}

/**
 * // Function: root が Modal Window 接続済みか判定
 */
export function isModalWindowMounted(root: HTMLElement | null): boolean
{
  if (!root)
  {
    return false;
  }

  return gModalWindowInstances.has(root);
}

function collectModalWindowRoots(scope: Document | HTMLElement, root_selector: string): HTMLElement[]
{
  const roots: HTMLElement[] = [];

  if (scope instanceof HTMLElement && scope.matches(root_selector))
  {
    roots.push(scope);
  }

  roots.push(...Array.from(scope.querySelectorAll<HTMLElement>(root_selector)));

  return Array.from(new Set(roots)).filter((root: HTMLElement) =>
  {
    const parent_root: HTMLElement | null = root.parentElement?.closest(root_selector) ?? null;
    return !parent_root;
  });
}
